import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthSession, useTranslation, type SupabaseClient } from '@dukanti/shared';
import {
  buildStorefrontNewsletterSubscribersCsv,
  storefrontNewsletterSubscribersAdminKeys,
  useStorefrontNewsletterSubscribersForAdmin,
  useSuppressStorefrontNewsletterSubscriberForAdmin,
  type StorefrontNewsletterSubscriberAdminRow,
} from '@dukanti/shared/admin';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { NativeSelect } from '@/features/website-templates/components/native-select';
import { NewsletterSubscriberSuppressDialog } from './newsletter-subscriber-suppress-dialog';

export interface NewsletterSubscribersPageProps {
  readonly client: SupabaseClient;
  readonly storeId: string;
  readonly onForbidden: () => void;
}

function isDenied(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'code' in error && error.code === '42501';
}

export function NewsletterSubscribersPage({
  client,
  storeId,
  onForbidden,
}: NewsletterSubscribersPageProps): React.JSX.Element {
  const { t, lang } = useTranslation();
  const queryClient = useQueryClient();
  const session = useAuthSession(client);
  const [limit, setLimit] = useState(50);
  const [target, setTarget] = useState<{
    identity: object;
    storeId: string;
    row: StorefrontNewsletterSubscriberAdminRow;
  } | null>(null);
  const [suppressError, setSuppressError] = useState(false);
  const [exportError, setExportError] = useState(false);
  const [denied, setDenied] = useState(false);
  const pending = useRef(false);
  const userId = session.user?.id ?? null;
  const nativeSession = session.session;
  const identity = useMemo(
    () => ({ storeId, client, userId, nativeSession }),
    [storeId, client, userId, nativeSession],
  );
  const [readyIdentity, setReadyIdentity] = useState<object | null>(null);
  const authorized = !denied && !session.isLoading && userId !== null && readyIdentity === identity;
  const live = useRef({ storeId, client, active: true, identity });
  live.current = { storeId, client, active: true, identity };
  const rowsQuery = useStorefrontNewsletterSubscribersForAdmin(client, {
    storeId: authorized ? storeId : null,
    limit,
  });
  const suppress = useSuppressStorefrontNewsletterSubscriberForAdmin(client);
  const queryDenied = isDenied(rowsQuery.failureReason ?? rowsQuery.error);
  const rows =
    authorized && !queryDenied && !rowsQuery.isLoading && !rowsQuery.isError
      ? (rowsQuery.data ?? [])
      : [];
  const selected = authorized && !queryDenied && target?.identity === identity ? target.row : null;

  useEffect(() => {
    live.current.active = true;
    setLimit(50);
    setTarget(null);
    setSuppressError(false);
    setExportError(false);
    setDenied(false);
    pending.current = false;
    const queryKey = storefrontNewsletterSubscribersAdminKeys.lists(storeId);
    void queryClient.cancelQueries({ queryKey });
    queryClient.removeQueries({ queryKey });
    setReadyIdentity(identity);
    return () => {
      if (live.current.storeId === storeId && live.current.client === client)
        live.current.active = false;
      const queryKey = storefrontNewsletterSubscribersAdminKeys.lists(storeId);
      void queryClient.cancelQueries({ queryKey });
      queryClient.removeQueries({ queryKey });
    };
  }, [client, storeId, queryClient, identity]);

  function forbidden(): void {
    setDenied(true);
    setTarget(null);
    setSuppressError(false);
    setExportError(false);
    const queryKey = storefrontNewsletterSubscribersAdminKeys.lists(storeId);
    void queryClient.cancelQueries({ queryKey });
    queryClient.removeQueries({ queryKey });
    onForbidden();
  }

  useEffect(() => {
    if (!authorized || !queryDenied) return;
    setDenied(true);
    setTarget(null);
    setSuppressError(false);
    setExportError(false);
    const queryKey = storefrontNewsletterSubscribersAdminKeys.lists(storeId);
    void queryClient.cancelQueries({ queryKey });
    queryClient.removeQueries({ queryKey });
    onForbidden();
  }, [authorized, queryDenied, client, storeId, queryClient, onForbidden]);

  async function confirm(): Promise<void> {
    if (!selected || selected.status !== 'active' || pending.current || denied) return;
    const origin = { ...live.current };
    const subscriberId = selected.id;
    const queryKey = storefrontNewsletterSubscribersAdminKeys.list(storeId, limit);
    const originQuery = queryClient.getQueryCache().find({ queryKey, exact: true });
    pending.current = true;
    setSuppressError(false);
    const currentScope = (): boolean =>
      live.current.active &&
      live.current.identity === origin.identity &&
      live.current.client === origin.client &&
      live.current.storeId === origin.storeId;
    try {
      const result = await suppress.mutateAsync({ storeId: origin.storeId, subscriberId });
      if (!currentScope()) return;
      if (
        originQuery &&
        queryClient.getQueryCache().find({ queryKey, exact: true }) === originQuery
      ) {
        queryClient.setQueryData<StorefrontNewsletterSubscriberAdminRow[]>(
          queryKey,
          (currentRows) => currentRows?.map((row) => (row.id === result.id ? result : row)),
        );
      }
      setTarget(null);
      toast.success(t('admin.newsletterSubscribers.suppressSuccess'));
    } catch (error) {
      if (!currentScope()) return;
      if (isDenied(error)) forbidden();
      else setSuppressError(true);
    } finally {
      if (currentScope()) pending.current = false;
    }
  }

  function download(): void {
    if (
      !live.current.active ||
      !authorized ||
      denied ||
      rowsQuery.isFetching ||
      rowsQuery.isError ||
      rows.length === 0
    )
      return;
    setExportError(false);
    let url: string | null = null;
    const anchor = document.createElement('a');
    try {
      url = URL.createObjectURL(
        new Blob([buildStorefrontNewsletterSubscribersCsv(rows)], {
          type: 'text/csv;charset=utf-8;',
        }),
      );
      anchor.href = url;
      anchor.download = 'displayed-subscribers.csv';
      document.body.append(anchor);
      anchor.click();
    } catch {
      setExportError(true);
    } finally {
      anchor.remove();
      if (url) URL.revokeObjectURL(url);
    }
  }

  const formatter = new Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short' });
  return (
    <section data-testid="newsletter-subscribers-page" className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div className="space-y-1.5">
          <Label htmlFor="newsletter-recent-limit">{t('admin.newsletterSubscribers.limit')}</Label>
          <NativeSelect
            id="newsletter-recent-limit"
            value={String(limit)}
            options={[50, 100, 500].map((count) => ({
              value: String(count),
              label: t('admin.newsletterSubscribers.limitOption', { count }),
            }))}
            onChange={(value) => {
              const next = Number(value);
              if (next === 50 || next === 100 || next === 500) {
                setLimit(next);
                setTarget(null);
                setExportError(false);
              }
            }}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={denied || rowsQuery.isFetching || rowsQuery.isError || rows.length === 0}
          onClick={download}
        >
          {t('admin.newsletterSubscribers.export')}
        </Button>
      </div>
      <p className="text-muted-foreground text-sm">
        {t('admin.newsletterSubscribers.recentLimit', { count: limit })}
      </p>
      {exportError ? (
        <p role="alert" className="text-destructive text-sm">
          {t('admin.newsletterSubscribers.exportError')}
        </p>
      ) : null}
      {denied || queryDenied ? (
        <p role="alert">{t('admin.newsletterSubscribers.forbidden')}</p>
      ) : !authorized || rowsQuery.isLoading ? (
        <div aria-busy="true" aria-label={t('common.loading')} className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : rowsQuery.isError ? (
        <div
          role="alert"
          className="border-border bg-card shadow-admin-card space-y-4 rounded-xl border p-6"
        >
          <p>{t('admin.newsletterSubscribers.loadError')}</p>
          <Button type="button" variant="outline" onClick={() => void rowsQuery.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <p className="border-border bg-card shadow-admin-card rounded-xl border p-6">
          {t('admin.newsletterSubscribers.empty')}
        </p>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => (
            <li
              key={row.id}
              className="border-border bg-card shadow-admin-card rounded-xl border p-4 sm:p-6"
              data-testid="newsletter-subscriber-row"
            >
              <dl className="grid min-w-0 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="min-w-0">
                  <dt className="text-muted-foreground text-sm">
                    {t('admin.newsletterSubscribers.contact')}
                  </dt>
                  <dd className="break-all font-medium">
                    <bdi dir="ltr">{row.contactValue}</bdi>
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-sm">
                    {t('admin.newsletterSubscribers.method')}
                  </dt>
                  <dd>{t(`admin.newsletterSubscribers.${row.contactType}`)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-sm">
                    {t('admin.newsletterSubscribers.status')}
                  </dt>
                  <dd>{t(`admin.newsletterSubscribers.${row.status}`)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground text-sm">
                    {t('admin.newsletterSubscribers.subscribedAt')}
                  </dt>
                  <dd>
                    <time dateTime={row.consentedAt}>
                      {formatter.format(new Date(row.consentedAt))}
                    </time>
                  </dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-muted-foreground text-sm">
                    {t('admin.newsletterSubscribers.consentVersion')}
                  </dt>
                  <dd className="break-all">
                    <bdi>{row.consentVersion}</bdi>
                  </dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-muted-foreground text-sm">
                    {t('admin.newsletterSubscribers.source')}
                  </dt>
                  <dd className="break-all">
                    <bdi>{row.sourceSectionId}</bdi>
                  </dd>
                </div>
              </dl>
              {row.status === 'active' ? (
                <div className="mt-4 flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={suppress.isPending}
                    onClick={() => {
                      setTarget({ storeId, identity, row });
                      setSuppressError(false);
                    }}
                  >
                    {t('admin.newsletterSubscribers.suppress')}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <NewsletterSubscriberSuppressDialog
        subscriber={selected}
        isPending={suppress.isPending}
        hasError={suppressError}
        onCancel={() => {
          if (!pending.current) {
            setTarget(null);
            setSuppressError(false);
          }
        }}
        onConfirm={() => void confirm()}
      />
    </section>
  );
}
